<script lang="ts">
  import type { Recommendation } from "$core/recommendations/recommendation.types";

  export let recommendations: Recommendation[];

  const PRIORITY_ORDER: Record<string, number> = {
    critical: 0,
    high: 1,
    medium: 2,
    low: 3,
  };

  const MODULE_LABELS: Record<string, string> = {
    "registro-de-ponto": "Registro de Ponto",
    "medidas-antifraude": "Medidas Antifraude",
    "calculos-e-tratamento": "Calculos e Tratamento",
    "fechamento-colaborativo": "Fechamento Colaborativo",
    "controle-de-custos": "Controle de Custos",
    "gestao-participativa": "Gestao Participativa",
    "reducao-de-riscos": "Reducao de Riscos",
  };

  $: sorted = [...recommendations].sort(
    (a, b) => (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99),
  );

  $: topItems = sorted.slice(0, 5);

  let copied = false;

  async function copyList(): Promise<void> {
    const text = topItems
      .map((r, i) => `${i + 1}. ${r.nextStep} (${MODULE_LABELS[r.module] ?? r.module})`)
      .join("\n");
    try {
      await navigator.clipboard.writeText(text);
      copied = true;
      setTimeout(() => {
        copied = false;
      }, 2000);
    } catch {
      // clipboard nao disponivel — silently fail
    }
  }
</script>

<section class="next-steps" aria-label="Proximos passos">
  <div class="next-steps__header">
    <h3 class="next-steps__title">Proximos Passos</h3>
    <button class="next-steps__copy-btn" type="button" on:click={copyList}>
      {copied ? "Copiado!" : "Copiar lista"}
    </button>
  </div>

  {#if topItems.length === 0}
    <p class="next-steps__empty">Nenhum proximo passo identificado.</p>
  {:else}
    <ol class="next-steps__list">
      {#each topItems as rec, idx (rec.code)}
        <li class="next-steps__item">
          <span class="next-steps__number" aria-hidden="true">{idx + 1}</span>
          <div class="next-steps__content">
            <p class="next-steps__text">{rec.nextStep}</p>
            <span class="next-steps__module">{MODULE_LABELS[rec.module] ?? rec.module}</span>
          </div>
        </li>
      {/each}
    </ol>
  {/if}
</section>

<style>
  .next-steps {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-4);
  }

  .next-steps__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ptt-space-3);
  }

  .next-steps__title {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--ptt-gray-300);
  }

  .next-steps__copy-btn {
    background: transparent;
    border: 1px solid var(--ptt-purple-100);
    border-radius: var(--ptt-radius-sm);
    color: var(--ptt-purple-100);
    font-size: 0.875rem;
    font-weight: 600;
    padding: var(--ptt-space-1) var(--ptt-space-3);
    cursor: pointer;
    transition: background 0.15s ease;
    white-space: nowrap;
  }

  .next-steps__copy-btn:hover {
    background: var(--ptt-gray-10);
  }

  .next-steps__empty {
    color: var(--ptt-gray-100);
    font-size: 0.9375rem;
    margin: 0;
  }

  .next-steps__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-3);
  }

  .next-steps__item {
    display: flex;
    align-items: flex-start;
    gap: var(--ptt-space-3);
  }

  .next-steps__number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    min-width: 28px;
    border-radius: 50%;
    background: var(--ptt-purple-100);
    color: var(--ptt-white);
    font-size: 0.8125rem;
    font-weight: 700;
  }

  .next-steps__content {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-1);
  }

  .next-steps__text {
    margin: 0;
    font-size: 0.9375rem;
    line-height: 1.5;
    color: var(--ptt-gray-300);
  }

  .next-steps__module {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--ptt-purple-100);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
</style>
